import { z } from 'zod';

/**
 * The ONE parse of `salesforce_account.primary_workspace_link` (Bucket A/E
 * plan, Key Technical Decision 4).
 *
 * `Primary_Workspace_Link__c` is a Salesforce `HYPERLINK()` formula, so the
 * mirror stores a full HTML anchor
 * (`<a href="https://app.clay.com/workspaces/19240" target="_blank">…</a>`),
 * not a bare URL. The numeric Clay workspace id exists ONLY inside that href:
 * `Primary_Workspace__c` is the raw `a01…` `Workspace__c` lookup id and is
 * never comparable to the numeric ids `resolve-contact-timeline` returns
 * (`docs/middleman-bucket-a-field-additions-ask.md` item 4 — its "Correction"
 * section). Deriving the id from `Primary_Workspace__c` marks every contact's
 * workspace as "not the main workspace"; don't.
 *
 * This runs SERVER-SIDE, at account row-shaping time (`src/api/flow.ts`), so
 * the wire carries the parsed `{ workspaceId, url }` and the frontend never
 * re-parses HTML. The ask doc explicitly calls for centralizing the parse here.
 */

/**
 * Parsed wire shape for `AccountRow.account_primary_workspace`. Schema-first:
 * the TS type is `z.infer`red, and `account-view.ts` embeds this same schema
 * rather than redeclaring the shape.
 */
export const PrimaryWorkspaceLinkSchema = z.object({
  /** Numeric Clay workspace id, as a string (SF ids are opaque; never math). */
  workspaceId: z.string(),
  /** Canonical `app.clay.com` workspace URL, rebuilt from the id. */
  url: z.string(),
});
export type PrimaryWorkspaceLink = z.infer<typeof PrimaryWorkspaceLinkSchema>;

/** `<a … href="…">` — same href-extraction idiom as `parseDomainLink`. */
const HREF_PATTERN = /href\s*=\s*["']([^"']+)["']/i;

/**
 * `/workspaces/<digits>` followed by a path/query/fragment boundary or the end
 * of the string, so `…/workspaces/19240/tables/tbl_x`, `…/workspaces/19240/`
 * and `…/workspaces/19240?x=1` all yield `19240`, while a non-numeric segment
 * (`/workspaces/a01Hs00001uDY3hIAG`) yields nothing.
 */
const WORKSPACE_ID_PATTERN = /\/workspaces\/(\d+)(?:[/?#]|$)/;

/**
 * Parse a raw `Primary_Workspace_Link__c` value into the numeric workspace id
 * plus a canonical URL. Returns `null` for null/blank/malformed input and
 * NEVER throws — this is a display field on an account row, so a weird value
 * must degrade to "no link", not 500 the whole accounts list.
 *
 * The returned `url` is REBUILT from the extracted digits rather than echoed
 * from the anchor. That is deliberate: the value crosses a trust boundary (an
 * SFDC formula an admin can edit) and React does not sanitize `href`, so
 * rebuilding makes a `javascript:`/`data:` href structurally unrepresentable
 * here instead of relying on a downstream scheme check.
 */
export function parsePrimaryWorkspaceLink(html: string | null | undefined): PrimaryWorkspaceLink | null {
  if (!html) return null;
  const trimmed = html.trim();
  if (!trimmed) return null;

  // Prefer the anchor's href; fall back to the whole string so a value stored
  // as a bare URL (or an anchor whose quoting we don't recognize) still works.
  const href = trimmed.match(HREF_PATTERN)?.[1] ?? trimmed;
  const workspaceId = href.match(WORKSPACE_ID_PATTERN)?.[1];
  if (!workspaceId) return null;

  return { workspaceId, url: `https://app.clay.com/workspaces/${workspaceId}` };
}
